// data-fit LLC · DecisionFlow · ADMIN Stage
// ParameterConfigEditor.jsx · DDL: R5 · API: v1.11 · Session 93
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: platform defaults read-only; scope_type tenant-only; parameter_value submitted as string
// Patches: PATCH-S93-PCE-01 (clarifying comment on parameter_value reset for boolean type)
// Open Items: OI-ADMIN-PARAM-PLATFORM, OI-ADMIN-PARAM-LIST, OI-ADMIN-PARAM-CREATE, OI-ADMIN-PARAM-PATCH
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './AdminFlowShell'
import { ChevronUp, ChevronDown, ChevronsUpDown, Search } from 'lucide-react'

const VALUE_TYPE = { TEXT: 'text', NUMERIC: 'numeric', BOOLEAN: 'boolean', INTEGER: 'integer', JSON: 'json' }
const SCOPE_TYPE = { PLATFORM: 'platform', TENANT: 'tenant' }

const INITIAL_CREATE_FORM = { parameter_value: '', effective_date: '', description: '' }
const INITIAL_EDIT_FORM   = { parameter_value: '', effective_date: '', end_date: '', description: '' }

const REQUIRED_CREATE_MESSAGE  = 'Select a platform parameter, enter a value, and provide an effective date.'
const INVALID_EDIT_DATE_MESSAGE = 'End date must be after effective date.'

const getErrorEnvelope = (err) => {
  if (err?.error) { const { code, message } = err.error; return { code, message } }
  return { code: 'UNKNOWN_ERROR', message: 'An unexpected error occurred.' }
}

const parseApiResponse = async (response) => {
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload?.error ? payload : { error: { code: 'HTTP_ERROR', http_status: response.status, message: 'Request failed.' } }
  }
  return payload
}

const getRowsFromEnvelope = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

export default function ParameterConfigEditor({ aiStatus, aiResponse, currentScreen, onNavigate }) {
  const { token } = useAuth()

  const [platformParams, setPlatformParams] = useState([])
  const [tenantParams, setTenantParams] = useState([])
  const [selectedParameterKey, setSelectedParameterKey] = useState('')
  const [selectedPlatformDefault, setSelectedPlatformDefault] = useState(null)
  const [createForm, setCreateForm] = useState(INITIAL_CREATE_FORM)
  const [editParameterId, setEditParameterId] = useState(null)
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM)
  const [isLoadingPlatform, setIsLoadingPlatform] = useState(false)
  const [isLoadingTenant, setIsLoadingTenant] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [formError, setFormError] = useState(null)
  const [editError, setEditError] = useState(null)

  const loadPlatformDefaults = useCallback(async () => {
    setIsLoadingPlatform(true)
    try {
      const response = await fetch('/api/v1/parameter-config?scope_type=platform', { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      return getRowsFromEnvelope(payload)
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      throw { error: { code, message: message || `Unable to load platform defaults. (${code})` } }
    } finally {
      setIsLoadingPlatform(false)
    }
  }, [token])

  const loadTenantOverrides = useCallback(async () => {
    setIsLoadingTenant(true)
    try {
      const response = await fetch('/api/v1/parameter-config?scope_type=tenant', { method: 'GET', credentials: 'include' })
      const payload = await parseApiResponse(response)
      const rows = getRowsFromEnvelope(payload)
      setTenantParams(rows)
      return rows
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      throw { error: { code, message: message || `Unable to load tenant overrides. (${code})` } }
    } finally {
      setIsLoadingTenant(false)
    }
  }, [token])

  const loadAllParameters = useCallback(async () => {
    setErrorMessage(null)
    try {
      const [platformRows, tenantRows] = await Promise.all([loadPlatformDefaults(), loadTenantOverrides()])
      setPlatformParams(platformRows)
      setTenantParams(tenantRows)
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setErrorMessage(message || `Unable to load parameter configuration. (${code})`)
    }
  }, [loadPlatformDefaults, loadTenantOverrides])

  useEffect(() => { loadAllParameters() }, [loadAllParameters])

  const handleParameterKeyChange = (event) => {
    const nextKey = event.target.value
    const matchingDefault = platformParams.find((p) => p.parameter_key === nextKey) || null
    setSelectedParameterKey(nextKey)
    setSelectedPlatformDefault(matchingDefault)
    setCreateForm((c) => ({ ...c, parameter_value: matchingDefault?.value_type === VALUE_TYPE.BOOLEAN ? 'false' : c.parameter_value }))
    setFormError(null)
  }

  const validateCreateForm = () => {
    if (!selectedParameterKey || !selectedPlatformDefault || !createForm.parameter_value || !createForm.effective_date) { setFormError(REQUIRED_CREATE_MESSAGE); return false }
    setFormError(null); return true
  }

  const validateEditForm = () => {
    if (editForm.end_date && editForm.effective_date && new Date(editForm.end_date) <= new Date(editForm.effective_date)) { setEditError(INVALID_EDIT_DATE_MESSAGE); return false }
    setEditError(null); return true
  }

  const handleCreateSubmit = async (event) => {
    event.preventDefault()
    if (!validateCreateForm()) return
    setIsSubmitting(true); setFormError(null)
    try {
      const body = { parameter_key: selectedParameterKey, parameter_value: String(createForm.parameter_value), value_type: selectedPlatformDefault.value_type, scope_type: SCOPE_TYPE.TENANT, effective_date: createForm.effective_date }
      if (createForm.description.trim() !== '') body.description = createForm.description
      const response = await fetch('/api/v1/parameter-config', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      await parseApiResponse(response)
      setSelectedParameterKey(''); setSelectedPlatformDefault(null)
      setCreateForm({ parameter_value: '', effective_date: '', description: '' })
      await loadTenantOverrides()
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setFormError(message || `Unable to create tenant override. (${code})`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartEdit  = (row) => { setEditParameterId(Number(row.parameter_id)); setEditForm({ parameter_value: String(row.parameter_value ?? ''), effective_date: row.effective_date || '', end_date: row.end_date || '', description: row.description || '' }); setEditError(null) }
  const handleCancelEdit = () => { setEditParameterId(null); setEditForm(INITIAL_EDIT_FORM); setEditError(null) }

  const handleSaveEdit = async (row) => {
    if (!validateEditForm()) return
    setIsSubmitting(true); setEditError(null)
    try {
      const parameterId = Number(row.parameter_id)
      const body = { parameter_value: String(editForm.parameter_value) }
      const response = await fetch(`/api/v1/parameter-config/${parameterId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      await parseApiResponse(response)
      setEditParameterId(null); setEditForm(INITIAL_EDIT_FORM)
      await loadTenantOverrides()
    } catch (err) {
      const { code, message } = getErrorEnvelope(err)
      setEditError(message || `Unable to update tenant override. (${code})`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputCls = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm'

  const renderValueInput = ({ valueType, value, onChange, id }) => {
    if (valueType === VALUE_TYPE.BOOLEAN) {
      return (
        <label className="flex items-center gap-2 text-tok-sm text-[rgb(var(--color-text))]" htmlFor={id}>
          <input id={id} type="checkbox" checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
          Enabled
        </label>
      )
    }
    if (valueType === VALUE_TYPE.NUMERIC) return <input id={id} type="number" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    if (valueType === VALUE_TYPE.INTEGER) return <input id={id} type="number" step="1" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    if (valueType === VALUE_TYPE.JSON) return <textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} rows={4} className={inputCls} />
    return <input id={id} type="text" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
  }

  const inlineCls = 'w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-2 py-1 text-tok-sm'

  // S579 — OI-UI-TABLE-CONSISTENCY
  const [platformSearch, setPlatformSearch] = useState('')
  const [platformSortCol, setPlatformSortCol] = useState('parameter_key')
  const [platformSortDir, setPlatformSortDir] = useState('asc')
  const [tenantSearch, setTenantSearch] = useState('')
  // No sort on tenant table — inline edit rows make sort a footgun mid-edit

  const togglePlatformSort = (col) => {
    if (platformSortCol === col) setPlatformSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setPlatformSortCol(col); setPlatformSortDir('asc') }
  }

  const SortIcon = ({ col, activeCol, activeDir }) => {
    if (activeCol !== col) return <ChevronsUpDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-text-muted))]" />
    return activeDir === 'asc'
      ? <ChevronUp className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5 text-[rgb(var(--color-primary))]" />
  }

  const filteredPlatform = useMemo(() => {
    const q = platformSearch.toLowerCase()
    return platformParams.filter((p) =>
      !q || (p.parameter_key ?? '').toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q)
    )
  }, [platformParams, platformSearch])

  const sortedPlatform = useMemo(() => {
    return [...filteredPlatform].sort((a, b) => {
      let av = '', bv = ''
      if (platformSortCol === 'parameter_key')   { av = a.parameter_key ?? '';   bv = b.parameter_key ?? '' }
      else if (platformSortCol === 'parameter_value') { av = a.parameter_value ?? ''; bv = b.parameter_value ?? '' }
      else if (platformSortCol === 'value_type') { av = a.value_type ?? '';      bv = b.value_type ?? '' }
      else if (platformSortCol === 'scope_type') { av = a.scope_type ?? '';      bv = b.scope_type ?? '' }
      else if (platformSortCol === 'effective_date') { av = a.effective_date ?? ''; bv = b.effective_date ?? '' }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return platformSortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredPlatform, platformSortCol, platformSortDir])

  const filteredTenant = useMemo(() => {
    const q = tenantSearch.toLowerCase()
    return tenantParams.filter((p) =>
      !q || (p.parameter_key ?? '').toLowerCase().includes(q) || (p.parameter_value ?? '').toLowerCase().includes(q)
    )
  }, [tenantParams, tenantSearch])

  const thCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] cursor-pointer select-none whitespace-nowrap hover:text-[rgb(var(--color-text))]'
  const thROCls = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] whitespace-nowrap'
  const tdCls = 'px-3 py-2.5 text-tok-sm text-[rgb(var(--color-text))]'

  // renderPlatformRows and renderTenantRows replaced by inline renders (S579)

  return (
    <div className="flex flex-col gap-4">
      <div className="mb-1">
        <h2 className="text-tok-base font-bold text-[rgb(var(--color-text))]">Parameter Config Editor</h2>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-0.5">Review platform defaults and maintain tenant-specific parameter overrides by parameter key.</p>
      </div>

      {errorMessage && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{errorMessage}</div>}

      {/* Create tenant override form */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Create Tenant Override</h3>
        <form className="flex flex-col gap-3" onSubmit={handleCreateSubmit}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]" htmlFor="parameter-key-select">
              Platform Parameter
              <select id="parameter-key-select" value={selectedParameterKey} onChange={handleParameterKeyChange} className={inputCls}>
                <option value="">Select a parameter</option>
                {platformParams.map((param) => <option key={param.parameter_key} value={param.parameter_key}>{param.parameter_key}</option>)}
              </select>
            </label>
            <div className="flex flex-col gap-1">
              <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">Inherited Value Type</span>
              <strong className="text-tok-sm text-[rgb(var(--color-text))]">{selectedPlatformDefault?.value_type || '—'}</strong>
            </div>
          </div>

          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]" htmlFor="create-parameter-value">
            Parameter Value
            {renderValueInput({ valueType: selectedPlatformDefault?.value_type || VALUE_TYPE.TEXT, value: createForm.parameter_value, onChange: (v) => setCreateForm((c) => ({ ...c, parameter_value: v })), id: 'create-parameter-value' })}
          </label>

          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]" htmlFor="create-effective-date">
            Effective Date
            <input id="create-effective-date" type="date" value={createForm.effective_date} onChange={(e) => setCreateForm((c) => ({ ...c, effective_date: e.target.value }))} className={`w-full max-w-xs ${inputCls}`} />
          </label>

          <label className="flex flex-col gap-1 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]" htmlFor="create-description">
            Description
            <textarea id="create-description" value={createForm.description} onChange={(e) => setCreateForm((c) => ({ ...c, description: e.target.value }))} rows={3} className={inputCls} />
          </label>

          {formError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{formError}</div>}

          <div>
            <button type="submit" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Override'}
            </button>
          </div>
        </form>
      </div>

      {/* Platform defaults table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Platform Defaults {platformParams.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({platformParams.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input type="text" placeholder="Search parameters…" value={platformSearch} onChange={(e) => setPlatformSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none" />
          </div>
        </div>
        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                {[['parameter_key','Parameter Key'],['parameter_value','Default Value'],['value_type','Value Type'],['scope_type','Scope'],['effective_date','Effective Date']].map(([col, label]) => (
                  <th key={col} className={thCls} onClick={() => togglePlatformSort(col)}>
                    {label}<SortIcon col={col} activeCol={platformSortCol} activeDir={platformSortDir} />
                  </th>
                ))}
                <th className={thROCls}>Description</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingPlatform && <tr><td colSpan="6" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading platform defaults…</td></tr>}
              {!isLoadingPlatform && sortedPlatform.length === 0 && <tr><td colSpan="6" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{platformSearch ? 'No parameters match your search.' : 'No platform defaults found.'}</td></tr>}
              {!isLoadingPlatform && sortedPlatform.map((param) => (
                <tr key={param.parameter_id || param.parameter_key} className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]">
                  <td className={`${tdCls} font-mono text-tok-xs`}>{param.parameter_key}</td>
                  <td className={tdCls}>{param.parameter_value}</td>
                  <td className={tdCls}>{param.value_type}</td>
                  <td className={tdCls}>{param.scope_type}</td>
                  <td className={tdCls}>{param.effective_date}</td>
                  <td className={`${tdCls} max-w-xs text-[rgb(var(--color-text-muted))]`}>{param.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tenant overrides table */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Tenant Overrides {tenantParams.length > 0 && <span className="ml-1 text-tok-xs font-normal text-[rgb(var(--color-text-muted))]">({tenantParams.length})</span>}</h3>
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
            <input type="text" placeholder="Search overrides…" value={tenantSearch} onChange={(e) => setTenantSearch(e.target.value)}
              className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] py-1.5 pl-8 pr-3 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none" />
          </div>
        </div>
        {editError && <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{editError}</div>}
        <div className="overflow-auto max-h-[calc(100vh-22rem)] rounded-xl border border-[rgb(var(--color-border))]">
          <table className="w-full text-tok-sm">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] z-10">
              <tr>
                <th className={thROCls}>Parameter Key</th>
                <th className={thROCls}>Platform Default</th>
                <th className={thROCls}>Tenant Value</th>
                <th className={thROCls}>Value Type</th>
                <th className={thROCls}>Effective Date</th>
                <th className={thROCls}>End Date</th>
                <th className={thROCls}>Description</th>
                <th className={thROCls}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingTenant && <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">Loading tenant overrides…</td></tr>}
              {!isLoadingTenant && filteredTenant.length === 0 && <tr><td colSpan="8" className="px-3 py-4 text-tok-sm text-[rgb(var(--color-text-muted))]">{tenantSearch ? 'No overrides match your search.' : 'No tenant overrides found.'}</td></tr>}
              {!isLoadingTenant && filteredTenant.map((row) => {
                const rowId = Number(row.parameter_id)
                const isEditing = editParameterId === rowId
                const platformDefaultValue = platformParams.find((p) => p.parameter_key === row.parameter_key)?.parameter_value || '—'
                return (
                  <tr key={rowId} className="border-t border-[rgb(var(--color-border))] hover:bg-[rgb(var(--color-surface-muted))]">
                    <td className={`${tdCls} font-mono text-tok-xs`}>{row.parameter_key}</td>
                    <td className={tdCls}>{platformDefaultValue}</td>
                    <td className={tdCls}>
                      {isEditing ? renderValueInput({ valueType: row.value_type, value: editForm.parameter_value, onChange: (v) => setEditForm((c) => ({ ...c, parameter_value: v })), id: `edit-parameter-value-${rowId}` }) : row.parameter_value}
                    </td>
                    <td className={tdCls}>{row.value_type}</td>
                    <td className={tdCls}>
                      {isEditing ? <input type="date" value={editForm.effective_date} onChange={(e) => setEditForm((c) => ({ ...c, effective_date: e.target.value }))} className={inlineCls} /> : row.effective_date}
                    </td>
                    <td className={tdCls}>
                      {isEditing ? <input type="date" value={editForm.end_date} onChange={(e) => setEditForm((c) => ({ ...c, end_date: e.target.value }))} className={inlineCls} /> : row.end_date || '—'}
                    </td>
                    <td className={tdCls}>
                      {isEditing ? <textarea value={editForm.description} onChange={(e) => setEditForm((c) => ({ ...c, description: e.target.value }))} rows={3} className={inlineCls} /> : row.description || '—'}
                    </td>
                    <td className={tdCls}>
                      {isEditing ? (
                        <div className="flex gap-2">
                          <button type="button" className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50" onClick={() => handleSaveEdit(row)} disabled={isSubmitting}>{isSubmitting ? 'Saving…' : 'Save'}</button>
                          <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={handleCancelEdit} disabled={isSubmitting}>Cancel</button>
                        </div>
                      ) : (
                        <button type="button" className="rounded-lg border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50" onClick={() => handleStartEdit(row)} disabled={isSubmitting}>Edit</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
